import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Database, Server, Cloud, CheckCircle2, XCircle, ArrowRightLeft,
  Download, Upload, Loader2, AlertTriangle, Shield, RefreshCw,
  ChevronLeft, PlugZap, HardDrive, Globe,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/* ═══════════ Collection count comparison row ═══════════ */
function CollRow({ name, source, target }) {
  const match = source === target;
  return (
    <div className={`flex items-center justify-between py-1.5 px-2 text-xs rounded ${match ? '' : 'bg-amber-50'}`}>
      <span className="font-medium w-44 truncate">{name}</span>
      <span className="w-16 text-right tabular-nums">{source ?? '-'}</span>
      <ArrowRightLeft className="w-3 h-3 text-zinc-300 mx-1" />
      <span className="w-16 text-right tabular-nums">{target ?? '-'}</span>
      {match ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 ml-1" /> : <XCircle className="w-3.5 h-3.5 text-amber-500 ml-1" />}
    </div>
  );
}

/* ═══════════ Status badge ═══════════ */
function HostBadge({ type }) {
  if (type === 'atlas') return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1"><Cloud className="w-3 h-3" /> Atlas</Badge>;
  if (type === 'local') return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 gap-1"><HardDrive className="w-3 h-3" /> Local</Badge>;
  return <Badge className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100 gap-1"><Globe className="w-3 h-3" /> Remote</Badge>;
}

/* ═══════════ MAIN PAGE ═══════════ */
export default function DatabaseHost() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Target connection form
  const [targetUrl, setTargetUrl] = useState('');
  const [targetDb, setTargetDb] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Migration flow
  const [step, setStep] = useState(0);
  // 0 = idle, 1 = tested target, 2 = exported, 3 = imported, 4 = verified, 5 = switched
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [switchDialog, setSwitchDialog] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [exportFile, setExportFile] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/db/status');
      setStatus(data);
    } catch { toast.error('Failed to load DB status'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  /* ── Test connection ── */
  const handleTest = async () => {
    if (!targetUrl.trim()) { toast.error('Enter a connection string'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/db/test-connection', {
        connection_string: targetUrl.trim(),
        db_name: targetDb.trim() || null,
      });
      setTestResult(data);
      if (data.success) {
        toast.success(data.message);
        setStep(1);
      } else {
        toast.error(data.message);
      }
    } catch (err) { toast.error('Test failed'); }
    finally { setTesting(false); }
  };

  /* ── Export current DB ── */
  const handleExport = async () => {
    setExporting(true);
    try {
      const resp = await api.post('/db/export', {}, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/json' });
      setExportFile(blob);
      // Also trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cashelblue_db_export_${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Database exported — file downloaded');
      setStep(2);
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  /* ── Import to target ── */
  const handleImport = async () => {
    if (!exportFile) { toast.error('Export first'); return; }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', exportFile, 'export.json');
      formData.append('connection_string', targetUrl.trim());
      formData.append('db_name', targetDb.trim() || '');
      const { data } = await api.post('/db/import-to-target', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        toast.success(`Imported ${data.total_imported} documents to target`);
        setStep(3);
      } else {
        toast.error('Import had issues');
      }
    } catch (err) {
      toast.error('Import failed');
    }
    finally { setImporting(false); }
  };

  /* ── Verify migration ── */
  const handleVerify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await api.post('/db/verify-migration', {
        connection_string: targetUrl.trim(),
        db_name: targetDb.trim() || null,
      });
      setVerifyResult(data);
      if (data.success && data.all_match) {
        toast.success('All collections match — safe to switch');
        setStep(4);
      } else if (data.success) {
        toast.warning('Some collections differ — review before switching');
        setStep(4);
      } else {
        toast.error(data.error || 'Verification failed');
      }
    } catch { toast.error('Verification failed'); }
    finally { setVerifying(false); }
  };

  /* ── Switch connection ── */
  const handleSwitch = async () => {
    setSwitching(true);
    try {
      const { data } = await api.post('/db/switch', {
        connection_string: targetUrl.trim(),
        db_name: targetDb.trim() || null,
      });
      if (data.success) {
        toast.success('Database switched! Server restart required.');
        setSwitchDialog(false);
        setStep(5);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Switch failed');
    }
    finally { setSwitching(false); }
  };

  /* ── Reset flow ── */
  const resetFlow = () => {
    setStep(0);
    setTestResult(null);
    setVerifyResult(null);
    setExportFile(null);
    setTargetUrl('');
    setTargetDb('');
    setConfirmText('');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const isAtlasTarget = targetUrl.includes('mongodb+srv') || targetUrl.includes('mongodb.net');

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="database-host-page">
      {/* ── Header ── */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/admin/data-management')} data-testid="back-to-data-mgmt">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-zinc-800">Database Host</h1>
          <p className="text-xs text-zinc-500">Manage and switch between local MongoDB and Atlas</p>
        </div>
      </div>

      {/* ── Current Status ── */}
      <Card data-testid="current-db-status">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              <CardTitle className="text-base">Current Connection</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <HostBadge type={status?.host_type} />
              {status?.connected
                ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100 gap-1"><CheckCircle2 className="w-3 h-3" /> Connected</Badge>
                : <Badge className="bg-red-100 text-red-700 hover:bg-red-100 gap-1"><XCircle className="w-3 h-3" /> Disconnected</Badge>
              }
            </div>
          </div>
          <CardDescription className="text-xs font-mono mt-1">{status?.connection_string_masked}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-3 text-sm">
            <span className="text-zinc-500">Database:</span>
            <span className="font-semibold">{status?.db_name}</span>
            <span className="text-zinc-400 mx-1">|</span>
            <span className="text-zinc-500">Documents:</span>
            <span className="font-semibold">{status?.total_documents?.toLocaleString()}</span>
          </div>
          {status?.collections && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {Object.entries(status.collections).map(([name, count]) => (
                <div key={name} className="flex items-center justify-between bg-zinc-50 rounded px-2 py-1.5 text-xs">
                  <span className="truncate text-zinc-600">{name}</span>
                  <span className="font-semibold ml-2 tabular-nums">{count}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Migration Panel ── */}
      <Card data-testid="migration-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-orange-500" />
            <CardTitle className="text-base">Switch Database</CardTitle>
          </div>
          <CardDescription className="text-xs">
            Safely migrate your data between local MongoDB and Atlas (or any MongoDB host)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Step indicator */}
          <div className="flex items-center gap-1 text-[10px] font-medium">
            {[
              { n: 1, label: 'Test Target' },
              { n: 2, label: 'Export Current' },
              { n: 3, label: 'Import to Target' },
              { n: 4, label: 'Verify' },
              { n: 5, label: 'Switch' },
            ].map(({ n, label }) => (
              <React.Fragment key={n}>
                {n > 1 && <div className={`flex-1 h-0.5 ${step >= n ? 'bg-blue-400' : 'bg-zinc-200'}`} />}
                <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${step >= n ? 'bg-blue-100 text-blue-700' : 'bg-zinc-100 text-zinc-400'}`}>
                  <span>{n}</span>
                  <span className="hidden sm:inline">{label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Connection form */}
          <div className="border rounded-lg p-4 space-y-3 bg-zinc-50/50">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold text-zinc-700 flex items-center gap-1.5">
                <PlugZap className="w-4 h-4" /> Target Connection
              </h3>
              {step > 0 && (
                <Button variant="ghost" size="sm" className="h-6 text-xs text-zinc-500" onClick={resetFlow}>
                  <RefreshCw className="w-3 h-3 mr-1" /> Reset
                </Button>
              )}
            </div>

            <div>
              <Label className="text-xs">Connection String</Label>
              <Input
                value={targetUrl}
                onChange={e => { setTargetUrl(e.target.value); setStep(0); setTestResult(null); }}
                placeholder="mongodb+srv://user:pass@cluster.mongodb.net/ or mongodb://localhost:27017/"
                className="mt-1 font-mono text-xs"
                data-testid="target-connection-input"
              />
              {isAtlasTarget && <p className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1"><Cloud className="w-3 h-3" /> Atlas connection detected</p>}
            </div>

            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label className="text-xs">Database Name <span className="text-zinc-400">(optional — defaults to current)</span></Label>
                <Input
                  value={targetDb}
                  onChange={e => setTargetDb(e.target.value)}
                  placeholder={status?.db_name || 'cashelblue_prod'}
                  className="mt-1 text-xs"
                  data-testid="target-db-name-input"
                />
              </div>
              <Button onClick={handleTest} disabled={testing || !targetUrl.trim()} className="h-9" data-testid="btn-test-connection">
                {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlugZap className="w-4 h-4 mr-1" />}
                Test Connection
              </Button>
            </div>

            {/* Test result */}
            {testResult && (
              <div className={`rounded-lg p-3 text-xs ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="test-result">
                <div className="flex items-center gap-1.5 font-semibold mb-1">
                  {testResult.success ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
                  {testResult.message}
                </div>
                {testResult.success && (
                  <div className="mt-2 text-zinc-600">
                    <p>Type: <strong>{testResult.host_type}</strong> | DB: <strong>{testResult.db_name}</strong> | Documents: <strong>{testResult.total_documents}</strong></p>
                    {testResult.total_documents > 0 && (
                      <p className="text-amber-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Target has existing data — import will overwrite it.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Migration steps — show after successful test */}
          {step >= 1 && (
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="text-sm font-semibold text-zinc-700 flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-blue-600" /> Safe Migration Steps
              </h3>

              {/* Step 2: Export */}
              <div className={`flex items-center justify-between p-3 rounded-lg border ${step >= 2 ? 'bg-green-50 border-green-200' : 'bg-white'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= 2 ? 'bg-green-500 text-white' : 'bg-zinc-200 text-zinc-600'}`}>2</div>
                  <div>
                    <p className="text-sm font-medium">Export Current Database</p>
                    <p className="text-[10px] text-zinc-500">Downloads a backup of your current data</p>
                  </div>
                </div>
                <Button onClick={handleExport} disabled={exporting || step >= 2} variant={step >= 2 ? 'outline' : 'default'} size="sm" data-testid="btn-export">
                  {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : step >= 2 ? <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-green-600" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                  {step >= 2 ? 'Exported' : 'Export'}
                </Button>
              </div>

              {/* Step 3: Import */}
              <div className={`flex items-center justify-between p-3 rounded-lg border ${step >= 3 ? 'bg-green-50 border-green-200' : step >= 2 ? 'bg-white' : 'opacity-50'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= 3 ? 'bg-green-500 text-white' : 'bg-zinc-200 text-zinc-600'}`}>3</div>
                  <div>
                    <p className="text-sm font-medium">Import to Target</p>
                    <p className="text-[10px] text-zinc-500">Transfers all data to the new database</p>
                  </div>
                </div>
                <Button onClick={handleImport} disabled={importing || step < 2 || step >= 3} variant={step >= 3 ? 'outline' : 'default'} size="sm" data-testid="btn-import">
                  {importing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : step >= 3 ? <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-green-600" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                  {step >= 3 ? 'Imported' : 'Import'}
                </Button>
              </div>

              {/* Step 4: Verify */}
              <div className={`flex items-center justify-between p-3 rounded-lg border ${step >= 4 ? 'bg-green-50 border-green-200' : step >= 3 ? 'bg-white' : 'opacity-50'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= 4 ? 'bg-green-500 text-white' : 'bg-zinc-200 text-zinc-600'}`}>4</div>
                  <div>
                    <p className="text-sm font-medium">Verify Migration</p>
                    <p className="text-[10px] text-zinc-500">Compares document counts between source and target</p>
                  </div>
                </div>
                <Button onClick={handleVerify} disabled={verifying || step < 3 || step >= 4} variant={step >= 4 ? 'outline' : 'default'} size="sm" data-testid="btn-verify">
                  {verifying ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : step >= 4 ? <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-green-600" /> : <Shield className="w-3.5 h-3.5 mr-1" />}
                  {step >= 4 ? 'Verified' : 'Verify'}
                </Button>
              </div>

              {/* Verification comparison table */}
              {verifyResult?.comparison && (
                <div className="border rounded-lg p-3" data-testid="verify-results">
                  <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold">
                    {verifyResult.all_match
                      ? <><CheckCircle2 className="w-4 h-4 text-green-600" /> All collections match</>
                      : <><AlertTriangle className="w-4 h-4 text-amber-500" /> Some collections differ</>
                    }
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-zinc-400 font-medium mb-1 px-2">
                    <span className="w-44">Collection</span>
                    <span className="w-16 text-right">Source</span>
                    <span className="w-5" />
                    <span className="w-16 text-right">Target</span>
                    <span className="w-4" />
                  </div>
                  <ScrollArea className="max-h-40">
                    {Object.entries(verifyResult.comparison).map(([name, { source, target, match }]) => (
                      <CollRow key={name} name={name} source={source} target={target} />
                    ))}
                  </ScrollArea>
                </div>
              )}

              {/* Step 5: Switch */}
              <div className={`flex items-center justify-between p-3 rounded-lg border-2 ${step >= 5 ? 'bg-green-50 border-green-300' : step >= 4 ? 'bg-amber-50 border-amber-300' : 'opacity-50 border-zinc-200'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= 5 ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'}`}>5</div>
                  <div>
                    <p className="text-sm font-semibold">Switch Active Database</p>
                    <p className="text-[10px] text-zinc-500">Updates the connection — requires server restart</p>
                  </div>
                </div>
                {step >= 5
                  ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100 gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Switched</Badge>
                  : <Button onClick={() => setSwitchDialog(true)} disabled={step < 4} variant="destructive" size="sm" data-testid="btn-switch-db">
                      <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Switch
                    </Button>
                }
              </div>

              {step >= 5 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                  <p className="font-semibold flex items-center gap-1"><Server className="w-4 h-4" /> Server restart required</p>
                  <p className="mt-1">The .env file has been updated. Restart the backend server to connect to the new database:</p>
                  <pre className="bg-blue-100 rounded p-2 mt-2 font-mono text-[10px]">
{`# Docker deployment:
docker compose -f docker/docker-compose.prod.yml restart backend

# Supervisor (development):
sudo supervisorctl restart backend`}
                  </pre>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Switch Confirmation Dialog ── */}
      <Dialog open={switchDialog} onOpenChange={setSwitchDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Confirm Database Switch
            </DialogTitle>
            <DialogDescription>
              This will update the server connection to point to the new database.
              The current database will remain untouched but will no longer be active.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-zinc-50 rounded-lg p-3 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-zinc-500">From:</span><span className="font-mono font-medium">{status?.host_type} ({status?.db_name})</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">To:</span><span className="font-mono font-medium">{isAtlasTarget ? 'atlas' : 'local/remote'} ({targetDb || status?.db_name})</span></div>
            </div>
            {verifyResult && !verifyResult.all_match && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-700 flex items-start gap-1.5">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Warning: Not all collections match. Some data may differ on the target.</span>
              </div>
            )}
            <div>
              <Label className="text-xs">Type <strong>SWITCH</strong> to confirm</Label>
              <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="SWITCH" className="mt-1" autoFocus data-testid="switch-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSwitchDialog(false); setConfirmText(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleSwitch} disabled={confirmText !== 'SWITCH' || switching} data-testid="btn-confirm-switch">
              {switching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
              Switch Database
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
